import { useState } from "react";
import { Check, FileText, ShieldCheck } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PermissionButton } from "@/components/ui/permission-button";
import { Textarea } from "@/components/ui/textarea";
import {
  useApproveFinalSettlementMutation,
  useGenerateFinalSettlementWpsMutation,
  useMarkFinalSettlementPaidMutation,
} from "@/services/(company)/payrollRunsApi";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { warnIfIbanChecksumFails } from "@/utils/payrollIbanWarning";

interface IProps {
  itemId: number;
  runId: number;
  runStatus: string;
  paymentMethod: string;
  hasPaymentFile: boolean;
  isPaid: boolean;
  onChanged: () => void;
}

const errorMessage = (err: unknown, fallback: string) => {
  const msg = (err as { data?: { message?: unknown } })?.data?.message;
  return Array.isArray(msg) ? msg.join(", ") : String(msg ?? fallback);
};

/**
 * Approve → (bank file) → paid, for one final settlement (B27, D6).
 *
 * A settlement is approved by someone other than the person who submitted it
 * (the API enforces it). A WPS or bank-transfer settlement is paid through its
 * payment file; closing one without a file asks for the reason, kept in the
 * audit log. Cash and cheque are marked paid directly once approved.
 */
export const SettlementPaymentActions = ({
  itemId,
  runId,
  runStatus,
  paymentMethod,
  hasPaymentFile,
  isPaid,
  onChanged,
}: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [approve, { isLoading: isApproving }] =
    useApproveFinalSettlementMutation();
  const [generate, { isLoading: isGenerating }] =
    useGenerateFinalSettlementWpsMutation();
  const [markPaid, { isLoading: isMarking }] =
    useMarkFinalSettlementPaidMutation();
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState("");

  const method = paymentMethod.toLowerCase();
  const isBankMethod = method === "wps" || method === "bank_transfer";
  const status = runStatus?.toUpperCase();

  if (isPaid) {
    return (
      <Badge variant="success" className="gap-1">
        <Check size={12} />
        {t("company:payrolls.paid")}
      </Badge>
    );
  }

  const handleApprove = async () => {
    try {
      await approve({ id: itemId }).unwrap();
      toast.success(t("company:payrolls.final_settlement_approved"));
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, t("common:states.error_title")));
    }
  };

  const handleGenerate = async () => {
    try {
      const res = await generate({ runIds: [runId] }).unwrap();
      warnIfIbanChecksumFails(res, t);
      toast.success(t("company:payrolls.payment_files_generated"));
      onChanged();
    } catch (err) {
      toast.error(
        errorMessage(err, t("company:payrolls.failed_to_generate_wps_files")),
      );
    }
  };

  const handleMarkPaid = async (note?: string) => {
    try {
      await markPaid({ id: itemId, note }).unwrap();
      toast.success(t("company:payrolls.final_settlement_marked_as_paid"));
      setReasonOpen(false);
      setReason("");
      onChanged();
    } catch (err) {
      toast.error(
        errorMessage(
          err,
          t("company:payrolls.failed_to_mark_final_settlement_as_paid"),
        ),
      );
    }
  };

  if (status === "DRAFT") {
    return (
      <PermissionButton
        type="button"
        section={PLATFORM_SECTION_ENUM.PAYROLL}
        action="edit"
        mode="disable"
        variant="primary"
        size="sm"
        className="gap-1.5"
        disabled={isApproving}
        onClick={handleApprove}
      >
        <ShieldCheck size={14} />
        {t("company:payrolls.approve_settlement")}
      </PermissionButton>
    );
  }

  const needsReason = isBankMethod && !hasPaymentFile;

  return (
    <>
      {needsReason && (
        <PermissionButton
          type="button"
          section={PLATFORM_SECTION_ENUM.PAYROLL}
          action="edit"
          mode="disable"
          variant="primary"
          size="sm"
          className="gap-1.5"
          disabled={isGenerating}
          onClick={handleGenerate}
        >
          <FileText size={14} />
          {t("company:payrolls.generate_payment_file")}
        </PermissionButton>
      )}
      <PermissionButton
        type="button"
        section={PLATFORM_SECTION_ENUM.PAYROLL}
        action="edit"
        mode="disable"
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={isMarking}
        onClick={() =>
          needsReason ? setReasonOpen(true) : handleMarkPaid(undefined)
        }
      >
        <Check size={14} />
        {isMarking
          ? t("common:states.marking")
          : t("company:payrolls.mark_as_paid")}
      </PermissionButton>

      <Dialog open={reasonOpen} onOpenChange={setReasonOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("company:payrolls.mark_paid_without_file_title")}
            </DialogTitle>
            <DialogDescription>
              {t("company:payrolls.mark_paid_without_file_desc")}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("company:payrolls.mark_paid_reason_placeholder")}
            aria-label={t("company:payrolls.mark_paid_reason_placeholder")}
            rows={3}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setReasonOpen(false)}
            >
              {t("common:buttons.cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={!reason.trim() || isMarking}
              onClick={() => handleMarkPaid(reason.trim())}
            >
              {t("company:payrolls.mark_as_paid")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
